import type { ProductVideoProps } from './ProductVideo';
import type { MouseEvent, PropsWithChildren } from 'react';

import dynamic from 'next/dynamic';
import { styled, css } from 'styled-components';

import { GALLERY_LAYOUT } from './utils/galleryLayout';
import type { ProductVideo as ProductVideoSchema } from '~/amplienceTypes/schemas/imported/product-video-schema';
import { ProductImage } from '~/components/pdp/productDetail/productImageGallery/ProductImage';
import type { CarouselImageProperty } from '~/components/pdp/productDetail/productImageGallery/ProductImage';
import { Box } from '~/components/shared/core/box/Box';
import type { Maybe, Video } from '~/utilities/graphql/codegen';

const ProductVideo = dynamic<ProductVideoProps>(
  () =>
    import('../../../pdp/productDetail/productImageGallery/ProductVideo').then(
      mod => mod.ProductVideo
    ),
  { ssr: false }
);

type GalleryItem =
  | {
      type: 'video';
      key: string;
    }
  | {
      type: 'image';
      key: string;
      index: number;
      original: CarouselImageProperty['original'];
      lazyLoad?: boolean;
    };

interface SmallDesktopLayoutProps {
  unpackedGallery: boolean;
  galleryItems: GalleryItem[];
  rowPattern: number[];
  visibleCount: number;
  showAllImages?: boolean;
  productImageList: CarouselImageProperty[];
  video?: Maybe<Video>;
  productVideoContent: ProductVideoSchema;
  setCurrentImageIndex: (idx: number) => void;
  setZoomPanelScrollXCenter: (e: MouseEvent) => void;
  gap: string;
  largeDesktopBreakpoint: number;
}

const S = {
  Grid: styled.div<{ $gap: string }>`
    ${({ $gap, theme }) => css`
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: ${$gap};
      padding: ${GALLERY_LAYOUT[theme.siteType].gap};
    `}
  `,
};

export const SmallDesktopLayout = ({
  unpackedGallery,
  galleryItems,
  rowPattern,
  visibleCount,
  showAllImages,
  productImageList,
  video,
  productVideoContent,
  setCurrentImageIndex,
  setZoomPanelScrollXCenter,
  gap,
  largeDesktopBreakpoint,
  children,
}: PropsWithChildren<SmallDesktopLayoutProps>) => {
  let itemIndex = 0;

  return (
    <S.Grid $gap={gap}>
      {unpackedGallery
        ? rowPattern.flatMap(rowSize => {
            const rowItems = galleryItems.slice(itemIndex, itemIndex + rowSize);
            const rowStartIndex = itemIndex;

            itemIndex += rowSize;

            return rowItems.map((item, colIndex) => {
              const absoluteIndex = rowStartIndex + colIndex;
              const isHidden = !showAllImages && absoluteIndex >= visibleCount;
              const isFullWidth = rowSize === 1;

              if (item.type === 'video') {
                return (
                  <Box
                    key={item.key}
                    $w="100%"
                    $h="auto"
                    aria-hidden={isHidden}
                    style={{
                      aspectRatio: '1 / 1',
                      overflow: 'hidden',
                      position: 'relative',
                      opacity: isHidden ? '0' : '1',
                      transition: 'opacity 0.3s ease-in-out',
                      transitionDelay: '0.3s',
                      gridColumn: isFullWidth ? '1 / -1' : undefined,
                    }}
                  >
                    {children}

                    <ProductVideo {...productVideoContent} active video={video} />
                  </Box>
                );
              }

              return (
                <Box
                  key={item.key}
                  $w="100%"
                  $h="auto"
                  aria-hidden={isHidden}
                  style={{
                    aspectRatio: '1 / 1',
                    backgroundColor: 'var(--image-background-placeholder)',
                    opacity: isHidden ? '0' : '1',
                    transition: 'opacity 0.3s ease-in-out',
                    transitionDelay: '0.3s',
                    gridColumn: isFullWidth ? '1 / -1' : undefined,
                  }}
                >
                  <Box
                    $w="100%"
                    $h="100%"
                    style={{
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                    onClick={(e: MouseEvent) => {
                      setCurrentImageIndex(item.index);
                      setZoomPanelScrollXCenter(e);
                    }}
                  >
                    {absoluteIndex === 0 && !video ? children : null}

                    <ProductImage
                      original={{
                        ...item.original,
                        tabIndex: isHidden ? -1 : 0,
                        dataImageIndex: item.index,
                      }}
                      largeDesktopBreakpoint={largeDesktopBreakpoint}
                      isFullWidthImage={isFullWidth}
                    />
                  </Box>
                </Box>
              );
            });
          })
        : productImageList.map(({ original, lazyLoad }, idx) => (
            <Box
              key={original.url}
              $w="100%"
              $h="auto"
              style={{
                overflow: 'hidden',
                aspectRatio: '1 / 1',
                gridColumn: idx % 3 === 0 ? '1 / -1' : undefined,
              }}
              onClick={(e: MouseEvent) => {
                setCurrentImageIndex(idx);
                setZoomPanelScrollXCenter(e);
              }}
            >
              <ProductImage
                original={original}
                lazyLoad={lazyLoad}
                largeDesktopBreakpoint={largeDesktopBreakpoint}
              />
            </Box>
          ))}
    </S.Grid>
  );
};
